import { Navigate, Outlet } from "react-router";

import { useAuthStore } from "@/client/stores/auth";

/**
 * FeatureGuard — automation surface visibility gate.
 *
 * Hides /jobs, /job-profile/*, /settings/auto-apply, /applications/*
 * from non-privileged users while VITE_HIDE_AUTOMATION_SURFACE=1.
 *
 * Bypass conditions (any one is sufficient):
 *   1. user.isAdmin — founder / admin accounts always see everything
 *   2. user.email in VITE_BETA_USER_EMAILS allowlist — beta testers
 *   3. user.subscription.status is "on_trial" or "TRIALING" — paying customers see it immediately
 *   4. user.subscription.status is "active" or "ACTIVE" — active paying subscribers
 *
 * This is a UI visibility gate, NOT an authorization boundary.
 * Server-side auto-apply endpoints enforce tier-based access independently.
 *
 * POST-APPROVAL CLEANUP:
 *   Once Lemon Squeezy approves, flip VITE_HIDE_AUTOMATION_SURFACE=0 and redeploy.
 *   Then delete this file, useAutomationSurfaceVisible, and remove the guard from the router.
 */
export const FeatureGuard = () => {
  const user = useAuthStore((state) => state.user);

  const hidden = import.meta.env.VITE_HIDE_AUTOMATION_SURFACE === "1";

  if (!hidden) return <Outlet />;

  const allowlist = (import.meta.env.VITE_BETA_USER_EMAILS ?? "")
    .split(",")
    .map((e: string) => e.trim().toLowerCase())
    .filter(Boolean);

  const subStatus = user?.subscription?.status.toLowerCase();
  const isPaying = subStatus === "active" || subStatus === "on_trial" || subStatus === "trialing";

  const bypass =
    user?.isAdmin === true ||
    (!!user?.email && allowlist.includes(user.email.toLowerCase())) ||
    isPaying;

  if (!bypass) return <Navigate replace to="/dashboard/resumes" />;

  return <Outlet />;
};
